/**
 * FilmSidesBadge — "which side(s) does this film carry?", one badge, one place.
 *
 * Phase 50.1 post-impl review (should-fix): the press station rendered its
 * "Both sides" and "Back only" badges with the SAME icon, variant and `text-xs`
 * size, so at arm's length under warehouse lighting the two read identically —
 * and pressing the wrong side of a back-only film is exactly the defect this
 * phase exists to stop. The DTG print station meanwhile carried a hand-rolled,
 * UNTRANSLATED `DUAL` literal for the same fact.
 *
 * Differentiation is carried by three channels at once (PRODUCTION_DESIGN §4.1
 * "never colour as the only information", §13):
 *   both sides → neutral secondary + <Layers/>       ("stacked designs")
 *   back only  → amber attention tint + <FlipVertical/> ("turn it over")
 * plus the text label itself, which is what an operator actually reads first.
 *
 * Sizing per PRODUCTION_DESIGN §4.2: `lg` (`text-base`, larger glyph) for
 * station surfaces where this fact drives the operator's next physical action —
 * the press station's card header. `sm` for a surface where a dedicated
 * full-width side banner already carries the state and this is a secondary
 * confirmation (the DTG print station's `PrintSideIndicator`).
 *
 * i18n note: the two labels live under the `pressing.*` keys they were born in
 * (`pressing.dualSides`, `pressing.backOnlyBadge`). They are station-neutral
 * copy — do NOT fork a second pair for another station; that duplication is
 * precisely what this component and the 50.1 locale cleanup removed.
 */
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Layers, FlipVertical } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FilmSides {
  /** The film carries BOTH a front and a back design. */
  isDual: boolean;
  /** The film carries ONLY the back design (a two-sided garment resolved with
   *  no front artwork). Mutually exclusive with `isDual` server-side. */
  isBackOnly: boolean;
}

export interface FilmSidesBadgeProps {
  sides: FilmSides;
  /** `lg` (default) where the fact drives the next physical action; `sm` where
   *  another element already states it prominently. */
  size?: 'sm' | 'lg';
  className?: string;
}

/**
 * Renders AT MOST ONE badge — `isDual` wins if both flags are somehow set, and
 * nothing renders for a plain front-only film (the default needs no badge; a
 * "Front only" pill on every card would be noise the operator learns to ignore).
 */
export function FilmSidesBadge({ sides, size = 'lg', className }: FilmSidesBadgeProps) {
  const { t } = useTranslation('production');
  const isLarge = size === 'lg';
  const sizeClasses = isLarge ? 'text-base font-semibold gap-1.5 px-3 py-1' : 'text-sm gap-1';
  const iconClasses = isLarge ? 'h-5 w-5' : 'h-3.5 w-3.5';

  if (sides.isDual) {
    return (
      <Badge variant="secondary" className={cn(sizeClasses, className)}>
        <Layers className={cn(iconClasses, 'shrink-0')} aria-hidden />
        {t('pressing.dualSides')}
      </Badge>
    );
  }

  if (sides.isBackOnly) {
    return (
      <Badge
        variant="outline"
        className={cn(
          // Amber = "wait, look at this" (§4.1). Distinct from the neutral
          // both-sides badge at a glance, and from the red REDO badge beside it.
          'border-amber-400 bg-amber-100 text-amber-900 dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-200',
          sizeClasses,
          className,
        )}
      >
        <FlipVertical className={cn(iconClasses, 'shrink-0')} aria-hidden />
        {t('pressing.backOnlyBadge')}
      </Badge>
    );
  }

  return null;
}
